import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";
import {
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
  CircularProgress,
  useMediaQuery,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip as MuiTooltip,
  Divider,
  Stack,
  Skeleton,
  Alert,
  Chip,
} from "@mui/material";
import { tokens } from "../../../theme";
import {
  InfoOutlined,
  BarChart as BarChartIcon,
  TableChart,
  Download,
  MonetizationOn,
  ErrorOutline,
} from "@mui/icons-material";

const CustomTooltip = ({ active, payload, label }) => {
  const theme = useTheme();

  if (active && payload && payload.length) {
    const dataItem = payload[0].payload;

    return (
      <Card
        sx={{
          backgroundColor: theme.palette.background.paper,
          boxShadow: theme.shadows[3],
          borderRadius: 2,
          p: 2,
          minWidth: 180,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          {dataItem.name} Days
        </Typography>
        <Divider sx={{ my: 1 }} />
        <Stack spacing={1}>
          <Box display="flex" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              Amount:
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              Rs.{dataItem.amount.toLocaleString()}
            </Typography>
          </Box>
        </Stack>
      </Card>
    );
  }
  return null;
};

const ReceivablesAgeingAnalysis = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode) || {
    greenAccent: { 500: "#4CAF50" },
    blueAccent: { 500: "#2196F3" },
    warning: "#FF9800",
  };
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const token = localStorage.getItem("token");
  const cmpid = localStorage.getItem("cmpid");

  // State management
  const [data, setData] = useState([
    { name: "Current", amount: 0 },
    { name: "1-30", amount: 0 },
    { name: "31-60", amount: 0 },
    { name: "61-90", amount: 0 },
    { name: "90+", amount: 0 },
  ]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUsingSampleData, setIsUsingSampleData] = useState(false);
  const [viewMode, setViewMode] = useState("chart");
  const [totalReceivables, setTotalReceivables] = useState(0);
  const [overduePercentage, setOverduePercentage] = useState(0);

  const CONFIG = {
    colors: {
      primary: [
        colors.greenAccent?.[500] || "#4CAF50",
        colors.blueAccent?.[500] || "#2196F3",
        colors.warning || "#FF9800",
        "#F44336",
        "#9C27B0",
      ],
      cardBg: theme.palette.background.default,
      textPrimary: theme.palette.text.primary,
      textSecondary: theme.palette.text.secondary,
    },
  };

  const generateSampleData = () => {
    const sampleData = [
      { name: "Current", amount: 118573 },
      { name: "1-30", amount: 1158 },
      { name: "31-60", amount: 0 },
      { name: "61-90", amount: 0 },
      { name: "90+", amount: 0 },
    ];

    const total = sampleData.reduce((sum, item) => sum + item.amount, 0);
    const overdue =
      (sampleData.slice(1).reduce((sum, item) => sum + item.amount, 0) /
        total) *
      100;

    return {
      data: sampleData,
      total: total,
      overdue: overdue,
    };
  };

  const calculateStats = (data) => {
    const total = data.reduce((sum, item) => sum + item.amount, 0);
    const overdue =
      total > 0
        ? (data.slice(1).reduce((sum, item) => sum + item.amount, 0) / total) *
          100
        : 0;

    return {
      total: total,
      overdue: overdue,
    };
  };

  const isDataEmpty = (data) => {
    // Check if all amounts are zero or if the API response is empty/undefined
    return (
      data.every((item) => item.amount === 0) || !data || data.length === 0
    );
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      setIsUsingSampleData(false);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/reports/ageingAnalysisofReceivables`,
        { cmpid },
        {
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
          timeout: 10000,
        }
      );

      const responseData = response.data["0"]; // Access the first object in the response

      // Check if response data exists and has values
      if (responseData) {
        const formattedData = [
          { name: "Current", amount: parseFloat(responseData.current) || 0 },
          { name: "1-30", amount: parseFloat(responseData.overdue30) || 0 },
          { name: "31-60", amount: parseFloat(responseData.overdue60) || 0 },
          { name: "61-90", amount: parseFloat(responseData.overdue90) || 0 },
          { name: "90+", amount: parseFloat(responseData.overdue120) || 0 },
        ];

        // Check if API data is empty (all values are zero)
        if (isDataEmpty(formattedData)) {
          const sampleData = generateSampleData();
          setData(sampleData.data);
          setTotalReceivables(sampleData.total);
          setOverduePercentage(sampleData.overdue);
          setIsUsingSampleData(true);
        } else {
          setData(formattedData);
          const stats = calculateStats(formattedData);
          setTotalReceivables(stats.total);
          setOverduePercentage(stats.overdue);
        }
      } else {
        // If API response is empty, use sample data
        const sampleData = generateSampleData();
        setData(sampleData.data);
        setTotalReceivables(sampleData.total);
        setOverduePercentage(sampleData.overdue);
        setIsUsingSampleData(true);
      }
    } catch (err) {
      console.error("Error fetching receivables ageing data:", err);
      setError(err.message || "Failed to load data");
      const sampleData = generateSampleData();
      setData(sampleData.data);
      setTotalReceivables(sampleData.total);
      setOverduePercentage(sampleData.overdue);
      setIsUsingSampleData(true);
    } finally {
      setLoading(false);
    }
  };

  const toggleViewMode = () => {
    setViewMode(viewMode === "chart" ? "table" : "chart");
  };

  const exportToCSV = () => {
    const headers = ["Age Bucket", "Amount"];
    const rows = data.map((item) => [item.name, item.amount]);

    let csvContent =
      "data:text/csv;charset=utf-8," +
      headers.join(",") +
      "\n" +
      rows.map((row) => row.join(",")).join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "receivables_ageing_analysis.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <Card
      sx={{
        height: "100%",
        minHeight: 500,
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[2],
        borderRadius: "12px",
        overflow: "hidden",
        border: `1px solid ${theme.palette.divider}`,
        display: "flex",
        flexDirection: "column",
        transition: "all 0.3s ease",
        "&:hover": {
          boxShadow: theme.shadows[6],
        },
      }}
    >
      <CardContent
        sx={{
          p: isMobile ? 2 : 3,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {/* Header Section */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
        >
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: CONFIG.colors.textPrimary,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <MonetizationOn
                sx={{
                  color: CONFIG.colors.primary[1],
                  fontSize: 28,
                }}
              />
              Receivables Ageing Analysis
              <MuiTooltip title="Breakdown of outstanding receivables by age">
                <InfoOutlined
                  sx={{
                    fontSize: 18,
                    color: CONFIG.colors.textSecondary,
                    cursor: "pointer",
                  }}
                />
              </MuiTooltip>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Distribution of outstanding invoices by days overdue
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Button
              size="small"
              onClick={toggleViewMode}
              startIcon={
                viewMode === "chart" ? <TableChart /> : <BarChartIcon />
              }
              sx={{
                textTransform: "none",
                color: CONFIG.colors.textSecondary,
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              {viewMode === "chart" ? "Table" : "Chart"}
            </Button>

            <Button
              size="small"
              onClick={exportToCSV}
              startIcon={<Download />}
              sx={{
                textTransform: "none",
                color: CONFIG.colors.textSecondary,
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              Export
            </Button>
          </Box>
        </Box>

        {/* Error/Sample Data Notice */}
        {error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {error} - Showing {isUsingSampleData ? "sample" : "partial"} data
            {isUsingSampleData && (
              <Typography variant="caption" display="block" mt={1}>
                Sample data may not reflect actual business metrics
              </Typography>
            )}
          </Alert>
        )}

        {isUsingSampleData && !loading && (
          <Box display="flex" alignItems="center" gap={1}>
            <Chip
              label="No data available. Showing sample data."
              color="warning"
              size="small"
              variant="outlined"
            />
            <Typography variant="caption" color="text.secondary">
              Sample data may not reflect actual business metrics
            </Typography>
          </Box>
        )}

        {/* Stats Summary */}
        <Box display="flex" flexDirection="column" gap={2}>
          {loading ? (
            <Skeleton variant="rounded" width="100%" height={120} />
          ) : (
            <Box
              display="grid"
              gridTemplateColumns={isMobile ? "1fr" : "repeat(2, 1fr)"}
              gap={2}
            >
              {/* Total Receivables Card */}
              <Paper
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: CONFIG.colors.cardBg,
                  border: `1px solid ${theme.palette.divider}`,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Total Receivables
                </Typography>
                <Typography variant="h5" fontWeight={700} mt={0.5}>
                  Rs.{totalReceivables.toLocaleString()}
                </Typography>
              </Paper>

              {/* Overdue Percentage Card */}
              <Paper
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: CONFIG.colors.cardBg,
                  border: `1px solid ${theme.palette.divider}`,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Overdue Receivables
                </Typography>
                <Typography variant="h5" fontWeight={700} mt={0.5}>
                  {overduePercentage.toFixed(1)}%
                </Typography>
              </Paper>
            </Box>
          )}
        </Box>

        {/* Chart/Table Area */}
        <Box flex={1} minHeight={300} position="relative" mt={2}>
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
              bgcolor={theme.palette.action.hover}
              borderRadius={2}
            >
              <CircularProgress size={isMobile ? 40 : 50} />
            </Box>
          ) : viewMode === "chart" ? (
            <Box
              height="100%"
              bgcolor={theme.palette.action.hover}
              borderRadius={2}
              p={2}
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.action.hover} 100%)`,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={data}
                  margin={{
                    top: 20,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={theme.palette.divider}
                  />
                  <XAxis
                    dataKey="name"
                    tick={{ fill: theme.palette.text.primary }}
                    axisLine={{ stroke: theme.palette.divider }}
                  />
                  <YAxis
                    tick={{ fill: theme.palette.text.primary }}
                    axisLine={{ stroke: theme.palette.divider }}
                  />
                  <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ fill: "rgba(0, 0, 0, 0.05)" }}
                  />
                  <Legend />
                  <Bar dataKey="amount" name="Amount (Rs.)">
                    {data.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={
                          CONFIG.colors.primary[
                            index % CONFIG.colors.primary.length
                          ]
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              {isUsingSampleData && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  textAlign="center"
                  mt={1}
                >
                  Displaying sample chart data
                </Typography>
              )}
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Age Bucket</TableCell>
                      <TableCell align="right">Amount (Rs.)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.map((row) => (
                      <TableRow key={row.name}>
                        <TableCell component="th" scope="row">
                          {row.name}
                        </TableCell>
                        <TableCell align="right">
                          Rs.{row.amount.toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow
                      sx={{ backgroundColor: theme.palette.action.hover }}
                    >
                      <TableCell
                        component="th"
                        scope="row"
                        sx={{ fontWeight: 700 }}
                      >
                        Total
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        Rs.
                        {data
                          .reduce((sum, item) => sum + item.amount, 0)
                          .toLocaleString()}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              {isUsingSampleData && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  textAlign="center"
                  mt={1}
                >
                  Displaying sample table data
                </Typography>
              )}
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default ReceivablesAgeingAnalysis;
